import { Stack } from '@mui/material';
import React from 'react';
import { generatePath } from 'react-router';

import { ResourceTypeDto } from '@/api/generated';
import {
  RowDetailsAllDone,
  RowDetailsError,
  RowDetailsLink,
  RowDetailsLoading,
  RowDetailsMore,
  RowDetailsMuted,
  RowDetailsSection,
} from '@/components/table/expandable/RowDetails.comp.tsx';
import {
  LIST_DETAILS_PREVIEW_SIZE,
  useActiveProcesses,
  useCustomerContactsPreview,
  useOpenResourceTasks,
  useSupplierContactsPreview,
} from '@/components/table/expandable/util/useListDetailsQueries.util.ts';
import { SmartDate } from '@/components/ui/smartDate/SmartDate.comp.tsx';
import { StatusDot } from '@/components/ui/statusPill/StatusPill.comp.tsx';
import { processStatusTone, taskStatusTone } from '@/components/ui/statusPill/statusTone.util.ts';
import { UserAvatar } from '@/components/ui/userAvatar/UserAvatar.comp.tsx';
import {
  personFromFullName,
  personFromUserOption,
} from '@/components/ui/userAvatar/userAvatar.util.ts';
import { useDictionariesContext } from '@/contexts/dictionaries/Dictionaries.context.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { useCanReadProcessData } from '@/utils/usePermissions.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

const PreviewRow: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
    {children}
  </Stack>
);

const PreviewTitle: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Stack
    sx={{
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Stack>
);

type OpenTasksProps = {
  moreTo?: string;
  resourceId: string;
  resourceTypes: ResourceTypeDto[];
};

export const OpenTasksSection: React.FC<OpenTasksProps> = ({
  moreTo,
  resourceId,
  resourceTypes,
}) => {
  const { t } = useTranslationWithPrefix('components.listDetails.sections');
  const { t: tStatus } = useTranslationWithPrefix('views.tasks.list.table.body');
  const { isError, isLoading, items, total } = useOpenResourceTasks({ resourceId, resourceTypes });

  return (
    <RowDetailsSection count={total} title={t('openTasks')}>
      {isLoading && <RowDetailsLoading />}
      {isError && <RowDetailsError />}
      {!isLoading && !isError && items.length === 0 && (
        <RowDetailsAllDone text={t('noOpenTasks')} />
      )}
      {items.slice(0, LIST_DETAILS_PREVIEW_SIZE).map(task => (
        <PreviewRow key={task.taskId}>
          <StatusDot
            label={tStatus(`taskStatusOptions.${task.status}`)}
            tone={taskStatusTone[task.status]}
          />
          <PreviewTitle>
            <RowDetailsLink to={generatePath(RouteEnum.TASK_DETAILS, { taskId: task.taskId })}>
              {task.title}
            </RowDetailsLink>
          </PreviewTitle>
          <SmartDate date={task.dueDate} variant="due" />
          <UserAvatar person={task.assignedUser} size={22} />
        </PreviewRow>
      ))}
      <RowDetailsMore
        count={total - Math.min(items.length, LIST_DETAILS_PREVIEW_SIZE)}
        to={moreTo}
      />
    </RowDetailsSection>
  );
};

type ActiveProcessesProps = {
  contactId?: number;
  customerId?: number;
};

export const ActiveProcessesSection: React.FC<ActiveProcessesProps> = props => {
  const canReadProcessData = useCanReadProcessData();

  if (!canReadProcessData) {
    return null;
  }

  return <ActiveProcessesSectionContent {...props} />;
};

const ActiveProcessesSectionContent: React.FC<ActiveProcessesProps> = ({
  contactId,
  customerId,
}) => {
  const { t } = useTranslationWithPrefix('components.listDetails.sections');
  const { t: tStatus } = useTranslationWithPrefix('views.processes.table.body');
  const { isError, isLoading, items, total } = useActiveProcesses({ contactId, customerId });

  return (
    <RowDetailsSection count={total} title={t('activeProcesses')}>
      {isLoading && <RowDetailsLoading />}
      {isError && <RowDetailsError />}
      {!isLoading && !isError && items.length === 0 && (
        <RowDetailsMuted text={t('noActiveProcesses')} />
      )}
      {items.map(process => (
        <PreviewRow key={process.processId}>
          <StatusDot
            label={tStatus(`processStatusOptions.${process.status}`)}
            tone={processStatusTone[process.status]}
          />
          <PreviewTitle>
            <RowDetailsLink
              to={generatePath(RouteEnum.PROCESSES_DETAILS, { processId: process.processId })}
            >
              {process.name}
            </RowDetailsLink>
          </PreviewTitle>
          <SmartDate date={process.createdAt} withTime={false} />
          <UserAvatar person={personFromFullName(process.owner)} size={22} />
        </PreviewRow>
      ))}
      <RowDetailsMore count={total - items.length} />
    </RowDetailsSection>
  );
};

type ContactsPreviewProps = {
  customerId?: number;
  supplierId?: number;
};

const ContactsPreviewList: React.FC<{
  isError: boolean;
  isLoading: boolean;
  items: ReturnType<typeof useCustomerContactsPreview>['items'];
  total: number;
}> = ({ isError, isLoading, items, total }) => {
  const { t } = useTranslationWithPrefix('components.listDetails.sections');
  const { USERS_SHORT } = useDictionariesContext();

  return (
    <RowDetailsSection count={total} title={t('contacts')}>
      {isLoading && <RowDetailsLoading />}
      {isError && <RowDetailsError />}
      {!isLoading && !isError && items.length === 0 && <RowDetailsMuted text={t('noContacts')} />}
      {items.map(contact => (
        <PreviewRow key={contact.contactId}>
          <PreviewTitle>
            <RowDetailsLink
              to={generatePath(RouteEnum.CONTACTS_DETAILS, {
                contactId: contact.contactId.toString(),
              })}
            >
              {`${contact.firstName} ${contact.lastName}`.trim() || contact.name}
            </RowDetailsLink>
          </PreviewTitle>
          <SmartDate date={contact.lastContactedAt} variant="relative" />
          <UserAvatar
            person={personFromUserOption(
              USERS_SHORT.find(user => user.value === contact.contactOwnerId),
            )}
            size={22}
          />
        </PreviewRow>
      ))}
      <RowDetailsMore count={total - items.length} />
    </RowDetailsSection>
  );
};

const CustomerContactsPreview: React.FC<{ customerId: number }> = ({ customerId }) => (
  <ContactsPreviewList {...useCustomerContactsPreview(customerId)} />
);

const SupplierContactsPreview: React.FC<{ supplierId: number }> = ({ supplierId }) => (
  <ContactsPreviewList {...useSupplierContactsPreview(supplierId)} />
);

export const ContactsPreviewSection: React.FC<ContactsPreviewProps> = ({
  customerId,
  supplierId,
}) => {
  if (customerId !== undefined) {
    return <CustomerContactsPreview customerId={customerId} />;
  }
  if (supplierId !== undefined) {
    return <SupplierContactsPreview supplierId={supplierId} />;
  }
  return null;
};
